import { useTranslation } from "react-i18next";
import { lazy, Suspense, useEffect, useState } from "react";
import { BET_PANELS, type BetPanel as BetPanelId, type RenderMode } from "@crashx/shared";
import type { ThemeManifest } from "@crashx/theme-schema";
import { selectConfig, useRoundStore } from "../../state/roundStore.js";
import { availableModes } from "../../game/modes/availability.js";
const GameCanvas = lazy(() =>
  import("../../ui/GameCanvas.js").then((module) => ({ default: module.GameCanvas })),
);
import { HistoryBar } from "../history/HistoryBar.js";
import { BetPanel } from "../bets/BetPanel.js";
import { BetList } from "../bets/BetList.js";
import { ChatPanel } from "../chat/index.js";
import { StrokeIcon } from "../../ui/StrokeIcon.js";
import { ModeSelector } from "./ModeSelector.js";
import { Link, ROUTES } from "../../app/router/index.js";
export interface GamePageProps {
  /** null while the theme pack is still loading. */
  theme: ThemeManifest | null;
}
/** The table: history, canvas with the multiplier, bet panels and the bet list. */
export function GamePage({ theme }: GamePageProps) {
  const { t } = useTranslation();
  const config = useRoundStore(selectConfig);
  const panels = config?.panels ?? 1;
  const configured = config?.renderMode ?? "graph";
  const available = availableModes(config?.availableRenderModes ?? [], theme);
  const operatorMode = available.includes(configured) ? configured : (available[0] ?? "graph");
  const [playerMode, setPlayerMode] = useState<RenderMode | null>(null);
  const canChooseMode = config?.allowPlayerRenderModeChange === true;
  const mode = canChooseMode && playerMode && available.includes(playerMode) ? playerMode : operatorMode;
  const [mobilePanel, setMobilePanel] = useState<BetPanelId>("A");
  const [chatOpen, setChatOpen] = useState(false);
  const visiblePanels = BET_PANELS.slice(0, panels);
  useEffect(() => {
    setPlayerMode(null);
  }, [canChooseMode, operatorMode]);
  return (
    <div className="cx-game-page" data-panel-finish={theme?.visual?.scenery.chrome}>
      <HistoryBar />
      <div className="cx-player-grid">
        <aside className="cx-player-bets">
          <BetList />
        </aside>
        <div className="cx-player-center">
          <section className="cx-game-stage">
            <div className="cx-stage-badges">
              {config?.fun ? <span>{t("balance.fun")}</span> : null}
              <span>{t("nav.provablyFair")}</span>
            </div>
            {canChooseMode ? (
              <ModeSelector mode={mode} available={available} onChange={setPlayerMode} />
            ) : null}
            {theme !== null ? (
              <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
                <GameCanvas theme={theme} mode={mode} />
              </Suspense>
            ) : (
              <div
                className="flex h-full items-center justify-center"
                style={{ color: "var(--cx-color-muted)" }}
              >
                {t("app.loading")}
              </div>
            )}
          </section>
          {visiblePanels.length > 1 ? (
            <div className="cx-mobile-panel-switch" role="tablist" aria-label={t("bet.panels")}>
              {visiblePanels.map((panel) => (
                <button
                  key={panel}
                  type="button"
                  role="tab"
                  aria-selected={mobilePanel === panel}
                  onClick={() => setMobilePanel(panel)}
                >
                  {t("bet.panel", { panel })}
                </button>
              ))}
            </div>
          ) : null}
          <div className="cx-bet-panels">
            {visiblePanels.map((panel) => (
              <div key={panel} className="cx-bet-panel-slot" data-mobile-active={mobilePanel === panel}>
                <BetPanel panel={panel} />
              </div>
            ))}
          </div>
        </div>
        <div className={chatOpen ? "cx-player-chat is-open" : "cx-player-chat"}>
          {config?.chatEnabled ? (
            <ChatPanel mobileOpen={chatOpen} onMobileClose={() => setChatOpen(false)} />
          ) : null}
        </div>
      </div>
      <nav className="cx-mobile-nav" aria-label={t("nav.mobile")}>
        <button type="button" aria-current="page" onClick={() => window.scrollTo({ top: 0 })}>
          <StrokeIcon name="game" />
          <span>{t("nav.game")}</span>
        </button>
        <button type="button" aria-pressed={chatOpen} onClick={() => setChatOpen(true)}>
          <StrokeIcon name="chat" />
          <span>{t("chat.title")}</span>
        </button>
        <Link to={ROUTES.wallet}>
          <StrokeIcon name="wallet" />
          <span>{t("nav.wallet")}</span>
        </Link>
        <button type="button" aria-disabled="true">
          <StrokeIcon name="profile" />
          <span>{t("nav.profile")}</span>
        </button>
      </nav>
    </div>
  );
}
